/// <reference lib="dom" />
import type { GalleryModelVariant } from '@/types/catalog'
import { VpDivider } from '@vtmn-play/react/headless'
import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DataFallback } from './DataFallback'
import './size-variations.scss'

interface SizeVariationsProps {
  sizes: GalleryModelVariant[]
}

export function SizeVariations({ sizes }: Readonly<SizeVariationsProps>) {
  const { t } = useTranslation()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const hiddenListRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    const hiddenList = hiddenListRef.current
    if (!wrapper || !hiddenList)
      return

    const checkOverflow = () => {
      const availableWidth = wrapper.clientWidth

      // Use the rendered hidden list width to include intrinsic content size
      // (including flex gaps/divider spacing) with fewer edge cases.
      const widthNeeded = hiddenList.scrollWidth

      setIsOverflowing(widthNeeded > availableWidth)
    }

    const resizeObserver = new ResizeObserver(() => checkOverflow())
    resizeObserver.observe(wrapper)

    const frameId = requestAnimationFrame(() => checkOverflow())

    return () => {
      cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
    }
  }, [sizes])

  return (
    <div ref={wrapperRef} className="product-variation-sizes-wrapper" data-testid="product-variation-sizes_wrapper">
      <span className="sr-only">
        {t('products-page.product-card.available-sizes')}
        :
        {' '}
      </span>
      <DataFallback
        value={sizes}
        fallbackKey="products-page.product-card.no-size"
        isEmpty={items => items.length === 0}
      >
        {items => (
          <div className="sizes-display">
            {isOverflowing && items.length > 1
              ? (
                  <>
                    {items[0].size}
                    {' '}
                    →
                    {' '}
                    {items.at(-1)?.size}
                  </>
                )
              : items.map(size => (
                  <div key={`product-size-${size.code}`} className="size-alignment">
                    {size.code !== items[0].code && (
                      <VpDivider orientation="vertical" />
                    )}
                    {size.size}
                  </div>
                ))}
          </div>
        )}
      </DataFallback>

      {/* control sample element to measure overflow */}
      <div
        ref={hiddenListRef}
        aria-hidden="true"
        className="sizes-overflow-sample-element"
      >
        {sizes.map(size => (
          <div key={`product-size-${size.code}`} className="size-alignment">
            {size.code !== sizes[0].code && (
              <VpDivider orientation="vertical" />
            )}
            {size.size}
          </div>
        ))}
      </div>
    </div>
  )
}
